import type { EditorThemeClasses } from 'lexical';

/**
 * Class names owned by RawTextarea rather than by the editor library.
 *
 * Everything that needs to find the editor or its blocks keys off these names,
 * so replacing the editor does not reach into unit helpers and test files.
 */
export const EDITOR_CLASS_NAME = 'raw-textarea-editor';
export const BLOCK_CLASS_NAME = 'raw-textarea-block';

/**
 * Carries no styling. `DraftEditor` is a match key, not a leftover — rename it
 * and accepted Sapling suggestions apply twice ("Frodo BagginsFrodo Baggins").
 *
 * Sapling decides how to write a suggestion by sniffing the element's class for
 * `DraftEditor` — Draft.js's own prefix. On a match it does the DOM write
 * itself; otherwise it dispatches a `beforeinput` *and* writes, which
 * double-applies in any editor that honours `beforeinput`. Lexical does, so it
 * needs the match. Sapling has no Lexical key of its own.
 *
 * Draft used to supply the class itself, so this matched by accident. Now it is
 * deliberate.
 */
export const SAPLING_COMPAT_CLASS_NAME = 'sapling-DraftEditor-compat';

/**
 * Every class name here is ours, so `semantic-ui/Textarea.less` can style the
 * editor without depending on names the editor library happens to emit.
 * Inline text formats need explicit classes too — without them bold and italic
 * carry no styling of their own.
 */
export const RAW_TEXTAREA_EDITOR_THEME = {
  code: 'raw-textarea-code-block',
  heading: {
    h1: `${BLOCK_CLASS_NAME} raw-textarea-heading-1`,
    h2: `${BLOCK_CLASS_NAME} raw-textarea-heading-2`,
    h3: `${BLOCK_CLASS_NAME} raw-textarea-heading-3`,
    h4: `${BLOCK_CLASS_NAME} raw-textarea-heading-4`,
    h5: `${BLOCK_CLASS_NAME} raw-textarea-heading-5`,
    h6: `${BLOCK_CLASS_NAME} raw-textarea-heading-6`,
  },
  link: 'raw-textarea-link',
  list: {
    checklist: 'raw-textarea-checklist',
    listitem: BLOCK_CLASS_NAME,
    listitemChecked: 'raw-textarea-list-item-checked',
    listitemUnchecked: 'raw-textarea-list-item-unchecked',
    nested: {
      listitem: 'raw-textarea-list-item-nested',
    },
    ol: 'raw-textarea-list raw-textarea-list-ordered',
    // Indexed by nesting depth, so a level can be targeted directly rather than
    // through a chain of descendant selectors.
    olDepth: [
      'raw-textarea-list-depth-1',
      'raw-textarea-list-depth-2',
      'raw-textarea-list-depth-3',
    ],
    ul: 'raw-textarea-list raw-textarea-list-unordered',
    ulDepth: [
      'raw-textarea-list-depth-1',
      'raw-textarea-list-depth-2',
      'raw-textarea-list-depth-3',
    ],
  },
  // Both classes: the shared block hook for anything block-level, plus a
  // paragraph-only hook, since `raw-textarea-block` is also on list items,
  // headings and quotes.
  ltr: 'raw-textarea-ltr',
  paragraph: `${BLOCK_CLASS_NAME} raw-textarea-paragraph`,
  quote: `${BLOCK_CLASS_NAME} raw-textarea-quote`,
  rtl: 'raw-textarea-rtl',
  table: 'raw-textarea-table',
  tableCell: 'raw-textarea-table-cell',
  tableCellHeader: 'raw-textarea-table-cell-header',
  tableRow: 'raw-textarea-table-row',
  text: {
    // `base` lands on every text run, formatted or not.
    base: 'raw-textarea-text',
    bold: 'raw-textarea-bold',
    code: 'raw-textarea-code',
    italic: 'raw-textarea-italic',
    strikethrough: 'raw-textarea-strikethrough',
    underline: 'raw-textarea-underline',
    underlineStrikethrough: 'raw-textarea-underline-strikethrough',
  },
} satisfies EditorThemeClasses;
